import { CharReveal, CountUp, formatPercent } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { PartyWrapped } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide10SkipperProps = {
  readonly wrapped: PartyWrapped;
};

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

const wholeSeconds = (ms: number): number => Math.round(ms / MS_PER_SECOND);

const clock = (ms: number): string => {
  const seconds = wholeSeconds(ms);

  return `${String(Math.floor(seconds / SECONDS_PER_MINUTE))}:${String(
    seconds % SECONDS_PER_MINUTE,
  ).padStart(2, '0')}`;
};

export const Slide10Skipper = ({ wrapped }: Slide10SkipperProps): ReactElement => {
  const { skipper } = wrapped;

  if (skipper === null) {
    const steadyStyle: StyleWithVars = { '--d': '250' };

    return (
      <>
        <p className="lede rise">Quickest on the skip button</p>
        <h2 className="mega">
          <CharReveal text="Nobody" delayMs={300} />
        </h2>
        <p className="lede rise" style={steadyStyle}>
          Every track that started got past halfway. Steady hands all night.
        </p>
      </>
    );
  }

  const { fastest } = skipper;
  const movedOnStyle: StyleWithVars = { '--d': '900' };
  const fillStyle: StyleWithVars = {
    '--p': String(Math.min(fastest.listenedMs / fastest.durationMs, 1)),
  };
  const footnoteStyle: StyleWithVars = { '--d': '2000' };
  const nameStyle: StyleWithVars = { '--chars': String(skipper.name.length) };

  return (
    <>
      <p className="lede rise">Quickest on the skip button</p>

      <h2 className="mega skipper-name" style={nameStyle}>
        <CharReveal text={skipper.name} delayMs={300} />
      </h2>

      <p className="lede rise" style={movedOnStyle}>
        moved on before halfway in{' '}
        <b>
          <CountUp to={skipper.skipFraction} format={formatPercent} delayMs={1100} />
        </b>{' '}
        of their tracks.
      </p>

      <div className="scrub" aria-hidden="true">
        <div className="scrub-bar">
          <i style={fillStyle} />
        </div>
        <div className="scrub-meta">
          <span>{clock(fastest.listenedMs)}</span>
          <span>{clock(fastest.durationMs)}</span>
        </div>
      </div>

      <p className="lede small rise" style={footnoteStyle}>
        Fastest skip of the night: {String(wholeSeconds(fastest.listenedMs))} seconds into{' '}
        {fastest.trackName}.
      </p>
    </>
  );
};
